import Invoice from '../../common/models/Invoice.ts';
import Driver from '../types/Driver.ts';
import PdfPageWithText from '../types/PdfPageWithText.ts';
import PdfProcessedPage from '../types/PdfProcessedPage.ts';
import PdfTextTest, { StringAtIndexTest } from '../types/PdfTextTest.ts';
import { compute_pdf_line_items, compute_pdf_tests } from '../pdf_parser.ts';
import { stamp } from '../pdf_writer.ts';
import PdfLineItem from '../types/PdfLineItem.ts';

export default class extends Driver {
    constructor() { super() }

    parse_project_NaN(pdf_text_contents: string[]): string {
        const tests = [
            {
                "string": "PO/ORDER",
                "elements_to_skip": 2
            } as StringAtIndexTest,
            {
                "string": "#",
                "elements_to_skip": 4
            } as StringAtIndexTest
        ]
    
        let pdf_tests = {
            "first_string": 'YOUR',
            "tests": tests,
            "elements_to_skip_to_return": 6
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_invoice_NaN(pdf_text_contents: string[]): string {
        const tests = [
            {
                "string": "INVOICE",
                "elements_to_skip": 2
            } as StringAtIndexTest,
            {
                "string": "NUMBER",
                "elements_to_skip": 4
            } as StringAtIndexTest
        ]

        let pdf_tests = {
            "first_string": 'OUR',
            "tests": tests,
            "elements_to_skip_to_return": 6
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) ?? ""
    }
    
    parse_total(pdf_text_contents: string[]): number | null {
        const tests = [
            {
                "string": "Due",
                "elements_to_skip": 2
            } as StringAtIndexTest
        ]

        let pdf_tests = {
            "first_string": 'Amount',
            "tests": tests,
            "elements_to_skip_to_return": 6
        } as PdfTextTest
    
        const total = compute_pdf_tests(pdf_text_contents, pdf_tests)
    
        return this.parse_money_string(total) ?? 0
    }

    parse_invoice_line_items(pdf_text_contents: string[]): PdfLineItem[] | null {
        const tests = [
            {
                "string": "QTY",
                "elements_to_skip": 2,
            }  as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'ORDER',
            "tests": tests,
            "elements_to_skip_to_return": 4
        } as PdfTextTest

        const line_item_parser = function (text_contents: string[], starting_index: number) {
            let line_items = []
            let current_index_cursor = starting_index 
            
            let qty_ordered: string[] = []
            let qty_shipped: string[] = []
            let price: string[] = []
            let units_of_measure: string[] = []
            let price_total: string[] = []
            let part_names: string[] = []
            let descriptions: string[] = []

            let line_count = 0

            do {
                qty_ordered.push(text_contents[current_index_cursor])
                current_index_cursor++
                line_count++
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "SHIP"
                && text_contents[current_index_cursor + 2] == " "
                && text_contents[current_index_cursor + 3] == "QTY"
                && text_contents[current_index_cursor + 4] == ""
            ) && current_index_cursor < text_contents.length)
            
            current_index_cursor += 5

            let line_count_index = 0
            do {
                qty_shipped.push(text_contents[current_index_cursor])
                current_index_cursor++
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "DESCRIPTION"
                && text_contents[current_index_cursor + 2] == ""
            ) && current_index_cursor < text_contents.length)

            current_index_cursor += 3

            line_count_index = 0

            let temp_description = ""

            do {
                if (text_contents[current_index_cursor + 1] == " ") {
                    temp_description += text_contents[current_index_cursor]
                    temp_description += text_contents[current_index_cursor + 1]
                    current_index_cursor += 2
                } else if (
                    text_contents[current_index_cursor + 1] != " "
                    && text_contents[current_index_cursor + 2] != " "
                ) {
                    temp_description += text_contents[current_index_cursor] + " "
                    current_index_cursor++
                } else {
                    temp_description += text_contents[current_index_cursor]
                    descriptions.push(temp_description)
                    part_names.push(temp_description)
                    temp_description = ""
                    current_index_cursor++
                }
            } while (!(
                text_contents[current_index_cursor] == "Unit"
                && text_contents[current_index_cursor + 1] == " "
                && text_contents[current_index_cursor + 2] == "Price/UM"
                && text_contents[current_index_cursor + 3] == ""
            ) && current_index_cursor < text_contents.length)

            line_count_index = 0

            current_index_cursor += 4

            do {
                let price_and_uom = text_contents[current_index_cursor].split('/')

                price.push(price_and_uom[0])
                units_of_measure.push(price_and_uom[1])
                
                current_index_cursor++
            } while (!(
                text_contents[current_index_cursor] == ""
                && text_contents[current_index_cursor + 1] == "Extension"
                && text_contents[current_index_cursor + 2] == ""
            ) && current_index_cursor < text_contents.length)

            current_index_cursor += 3

            do {
                price_total.push(text_contents[current_index_cursor])
                current_index_cursor++
                line_count_index++
            } while (line_count_index < line_count)
            
            line_count_index = 0

            while(line_count_index < line_count) {
                let new_line_item = {
                    "part_name": part_names[line_count_index],
                    "quantity": parseInt(qty_ordered[line_count_index]),
                    "quantity_shipped": parseInt(qty_shipped[line_count_index]),
                    "unit_of_measure": units_of_measure[line_count_index],
                    "price_per_unit": parseFloat(price[line_count_index]),
                    "line_item_total": parseFloat(price_total[line_count_index]),
                    "part_description": descriptions[line_count_index]
                } as PdfLineItem

                line_items.push(new_line_item)
                line_count_index++
            }

            return line_items
        }
    
        return compute_pdf_line_items(pdf_text_contents, pdf_tests, line_item_parser) ?? null
    }

    check(pdf_text_contents: string[]): boolean {
        const tests = [
            {
                "string": "METER",
                "elements_to_skip": 2
            } as StringAtIndexTest,
        ]

        let pdf_tests = {
            "first_string": 'VAN',
            "tests": tests,
            "elements_to_skip_to_return": 4
        } as PdfTextTest

        return compute_pdf_tests(pdf_text_contents, pdf_tests) === "INC."
    }

    parse_pages(pages: PdfPageWithText[]): PdfProcessedPage[] {
        return pages.map((page: PdfPageWithText) => {
            return new PdfProcessedPage({
                page_number: page.page_number,
                vendor: 'Van Meter',
                line_items: this.parse_invoice_line_items(page.text_contents),
                project_NaN: this.parse_project_NaN(page.text_contents),
                invoice_NaN: this.parse_invoice_NaN(page.text_contents),
                total: this.parse_total(page.text_contents)
            })
        })
    }

    async stamp_invoices(invoices: Invoice[]): Promise<Invoice[]> {
        const x_coordinate = 140
        const y_coordinate = 791

        for (const index in invoices) {
            const invoice = invoices[index]
            if (invoice.pdf_bytes === null) {
                continue
            }

            const stamped_pdf_bytes = await stamp(x_coordinate, y_coordinate, invoice.invoice_information, invoice.pdf_bytes)
            invoices[index].stamped_pdf_bytes = stamped_pdf_bytes
            invoices[index].stamp_x = x_coordinate
            invoices[index].stamp_y = y_coordinate
        }

        return invoices
    }
}

